import { apiInstance, buyerCaptureException } from '@diamond/shared/utils';
import { AxiosError, AxiosRequestConfig } from 'axios';

import { captureAdminDataAccessError } from './capture-admin-data-access-error';

/**
 * Creates a wrapper for making POST requests to an API endpoint with error handling and exception capture.
 *
 * @param url - The URL endpoint for the POST request
 * @param token - Optional authentication token to be used with the API request
 * @param withoutStringify - Optional flag to skip JSON.stringify on the payload:
 *   - true: Sends payload as-is (useful for FormData, File uploads, etc.)
 *   - false/undefined: Applies JSON.stringify to the payload (default behavior)
 * @param captureException - Optional exception capture mode:
 *   - 'admin': Captures exceptions using the shared admin API capture helper
 *   - 'buyer': Captures exceptions using buyerCaptureException
 *   - undefined: No exception capturing
 *
 * @returns Object containing the postData function for making POST requests
 *
 * @example
 * ```typescript
 * // Basic JSON POST
 * const { postData } = PostDataWrapper(
 *   '/api/users',
 *   'your-auth-token',
 *   false,
 *   'admin'
 * );
 * const result = await postData({ name: 'John', email: 'john@example.com' });
 *
 * // File upload without stringify
 * const { postData } = PostDataWrapper(
 *   '/api/upload',
 *   'your-auth-token',
 *   true, // Skip JSON.stringify for file uploads
 *   'buyer'
 * );
 * const formData = new FormData();
 * formData.append('file', fileBlob);
 * const result = await postData(formData);
 * ```
 */
export function PostDataWrapper(
  url: string,
  token?: string,
  withoutStringify?: boolean,
  captureException?: 'admin' | 'buyer'
) {
  /**
   * Makes a POST request to the configured endpoint with the provided payload.
   *
   * @template T - The type of the payload being sent
   * @param payload - The data to be sent in the POST request body
   * @param config - Optional Axios request configuration to customize the request
   *   (e.g., headers, timeout, responseType, etc.)
   *
   * @returns Promise resolving to the API response data (unknown type)
   *
   * @throws Error with the API error message when captureException is enabled
   *
   * @example
   * ```typescript
   * // Basic POST with JSON payload
   * const result = await postData({ name: 'John', age: 30 });
   *
   * // POST with custom headers
   * const result = await postData(
   *   { name: 'John' },
   *   { headers: { 'Content-Type': 'application/json' } }
   * );
   *
   * // File upload (requires withoutStringify: true in wrapper)
   * const formData = new FormData();
   * formData.append('file', file);
   * const result = await postData(formData);
   *
   * // POST with timeout configuration
   * const result = await postData(
   *   { data: 'example' },
   *   { timeout: 5000 }
   * );
   * ```
   */
  async function postData<T>(
    payload: T,
    config?: AxiosRequestConfig
  ): Promise<unknown> {
    try {
      const response = await apiInstance(token, config).post(
        url,
        withoutStringify ? payload : JSON.stringify(payload)
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        if (captureException === 'admin') {
          const responseData = error.response?.data;
          throw captureAdminDataAccessError({
            error,
            message: responseData?.message,
            requestPath: url,
            httpMethod: 'POST',
            queryKey: 'PostDataWrapper error',
            operation: 'post_data',
            extras: { payload },
            level: 'error',
          });
        }
        if (captureException === 'buyer') {
          buyerCaptureException(error.response?.data, (scope) => {
            scope.setTags({
              queryKey: 'PostDataWrapper error',
              status_code:
                error instanceof AxiosError
                  ? error.response?.data.statusCode
                  : undefined,
            });
            scope.setContext('error', {
              error:
                error instanceof AxiosError ? error.response?.data : undefined,
            });
            scope.setExtra(url, payload);
            scope.setLevel('error');
          });
          throw new Error(error.response?.data.message);
        }
      }
      return;
    }
  }
  return { postData };
}
